import type { CccdParameterType } from '@/lib/cccdScoringParameters';
import type { ParameterConfigValues } from '@/components/cccd/ParameterConfigFields';

// Copia deliberada (no importada) de las fórmulas puras de
// platforms/api/src/services/cccd-scoring.ts — esto es solo una VISTA
// PREVIA ilustrativa al crear/editar un parámetro (cccd-scoring-ux-redesign),
// no el cálculo real de puntaje; si cambia una fórmula ahí, hay que
// actualizar esta también para que el ejemplo no mienta.
const round2 = (n: number) => Math.round(n * 100) / 100;

function num(values: ParameterConfigValues, field: keyof ParameterConfigValues, fallback: number): number {
  const raw = values[field];
  if (typeof raw !== 'string' || raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

function heightWithMaxError(raw: number, target: number, maxError: number, maxScore: number): number {
  if (target === 0 || maxError === 0) return 0;
  return Math.max(0, round2(maxScore * (1 - Math.abs(raw - target) / maxError)));
}

function perfectBandScore(raw: number, targetH0: number, perfectBandPct: number, maxErrorPct: number, maxScore: number): number {
  if (targetH0 === 0) return 0;
  const error = Math.abs(raw - targetH0) / targetH0;
  const perfectBand = perfectBandPct / 100;
  const maxError = maxErrorPct > 1 ? maxErrorPct / 100 : maxErrorPct;
  let score: number;
  if (error <= perfectBand) score = maxScore;
  else if (error <= maxError && maxError > perfectBand) score = maxScore * (1 - (error - perfectBand) / (maxError - perfectBand));
  else score = 0;
  if (!Number.isFinite(score)) score = 0;
  return Math.max(0, Math.min(maxScore, round2(score)));
}

function flightTimeScore(raw: number, minTime: number, maxTime: number, tolerance: number, maxScore: number): number {
  if (raw >= minTime && raw <= maxTime) return round2(maxScore / 2);
  const avg = (minTime + maxTime) / 2;
  const score = tolerance === 0 ? 0 : maxScore * (1 - Math.abs(raw - avg) / tolerance);
  return Math.max(0, Math.min(maxScore, round2(score)));
}

function trigHeight(angleDeg: number, adjustment: number): number {
  return round2(100 * Math.tan((angleDeg * Math.PI) / 180) + adjustment);
}

export interface PreviewLine {
  /** Lo que anota el jurado (o el dato de origen), en lenguaje simple. */
  input: string;
  /** El puntaje resultante, ya explicado. */
  result: string;
}

export interface ParameterTypePreview {
  /** Frase corta para el selector de tipo — sin jerga. */
  cardBlurb: string;
  /** Explicación de 1-2 frases de cómo funciona, para alguien no técnico. */
  howItWorks: string;
  /** Una o más líneas de ejemplo, calculadas con los valores reales del formulario (o valores de ejemplo si están vacíos). */
  lines(values: ParameterConfigValues): PreviewLine[];
}

export const PARAMETER_TYPE_PREVIEWS: Record<CccdParameterType, ParameterTypePreview> = {
  numeric_value: {
    cardBlurb: 'El jurado escribe un número y ese número es el puntaje, tal cual.',
    howItWorks: 'No hay fórmula ni configuración: lo que anote el jurado es directamente el puntaje del equipo en este parámetro.',
    lines: () => [
      { input: 'El jurado escribe: 87', result: 'Puntaje del equipo: 87' },
    ],
  },
  yes_no_parameter: {
    cardBlurb: 'El jurado solo marca Sí o No. Sí da puntos, No da cero.',
    howItWorks: 'Defines cuántos puntos vale un "Sí". Si el jurado marca "No", el equipo no gana puntos en este parámetro.',
    lines: (values) => {
      const maxValue = num(values, 'maxValue', 50);
      return [
        { input: 'El jurado marca: Sí', result: `Puntaje del equipo: ${maxValue}` },
        { input: 'El jurado marca: No', result: 'Puntaje del equipo: 0' },
      ];
    },
  },
  height_with_max_error: {
    cardBlurb: 'El jurado mide una altura. Entre más cerca del objetivo, más puntos.',
    howItWorks: 'Defines la altura objetivo y cuánto margen de error se permite. El equipo pierde puntos proporcionalmente mientras más se aleje del objetivo, hasta llegar a 0 en el error máximo.',
    lines: (values) => {
      const target = num(values, 'targetHeight', 200);
      const maxError = num(values, 'maxError', 50);
      const maxScore = num(values, 'maxScore', 100);
      const sampleRaw = round2(target - maxError * 0.4);
      const score = heightWithMaxError(sampleRaw, target, maxError, maxScore);
      return [
        { input: `El jurado mide: ${sampleRaw} m (objetivo: ${target} m)`, result: `Puntaje del equipo: ${score} de ${maxScore}` },
      ];
    },
  },
  height_with_perfect_band: {
    cardBlurb: 'Como el anterior, pero el objetivo lo calcula el sistema según el motor del equipo.',
    howItWorks: 'El objetivo no lo escribes tú — el sistema lo toma del motor de vuelo que tenga asignado el equipo. Si cae dentro de la "zona perfecta" (un % del objetivo), el equipo se lleva el puntaje completo; más allá de ahí, va bajando hasta 0.',
    lines: (values) => {
      const perfectBand = num(values, 'perfectBand', 5);
      const maxError = num(values, 'maxError', 20);
      const maxScore = num(values, 'maxScore', 150);
      const sampleTarget = 300; // ilustrativo: vendría del motor asignado al equipo, no se anota a mano
      const perfectRaw = round2(sampleTarget * (1 + (perfectBand / 100) * 0.6));
      const perfectScore = perfectBandScore(perfectRaw, sampleTarget, perfectBand, maxError, maxScore);
      const outsideRaw = round2(sampleTarget * (1 + ((perfectBand + maxError) / 2 / 100)));
      const outsideScore = perfectBandScore(outsideRaw, sampleTarget, perfectBand, maxError, maxScore);
      return [
        { input: `Motor del equipo → objetivo: ${sampleTarget} m. El jurado mide: ${perfectRaw} m (dentro de la zona perfecta)`, result: `Puntaje del equipo: ${perfectScore} de ${maxScore} (completo)` },
        { input: `Otro equipo mide: ${outsideRaw} m (fuera de la zona perfecta, dentro del error máximo)`, result: `Puntaje del equipo: ${outsideScore} de ${maxScore}` },
      ];
    },
  },
  flight_time: {
    cardBlurb: 'El jurado cronometra el vuelo. Caer en el rango esperado da la mitad del puntaje.',
    howItWorks: 'Defines una ventana de tiempo esperada (mínimo–máximo). Si el vuelo dura eso, el equipo gana la mitad del puntaje máximo. Si dura más o menos, va bajando según la tolerancia que definas.',
    lines: (values) => {
      const minTime = num(values, 'minTime', 8);
      const maxTime = num(values, 'maxTime', 12);
      const tolerance = num(values, 'tolerance', 5);
      const maxScore = num(values, 'maxScore', 100);
      const inWindow = round2((minTime + maxTime) / 2);
      const inScore = flightTimeScore(inWindow, minTime, maxTime, tolerance, maxScore);
      const outside = round2(maxTime + tolerance);
      const outScore = flightTimeScore(outside, minTime, maxTime, tolerance, maxScore);
      return [
        { input: `El jurado cronometra: ${inWindow} s (dentro del rango ${minTime}–${maxTime} s)`, result: `Puntaje del equipo: ${inScore} de ${maxScore}` },
        { input: `Otro equipo: ${outside} s (fuera del rango)`, result: `Puntaje del equipo: ${outScore} de ${maxScore}` },
      ];
    },
  },
  calculated_height: {
    cardBlurb: 'El jurado no mide la altura — mide 3 ángulos, y el sistema la calcula.',
    howItWorks: 'El jurado apunta un instrumento al cohete y anota 3 ángulos (no una altura). El sistema calcula la altura con trigonometría. El puntaje no depende solo de esa altura: se reparte proporcionalmente — el equipo que llegó más alto en la categoría se lleva el puntaje completo, los demás una fracción de eso.',
    lines: (values) => {
      const maxScore = num(values, 'maxScore', 150);
      const h1 = trigHeight(60, 2);
      const h2 = trigHeight(61, -1);
      const h3 = trigHeight(59, 0);
      const avg = round2((h1 + h2 + h3) / 3);
      return [
        { input: `El jurado mide 3 ángulos: 60°, 61°, 59° (con pequeños ajustes)`, result: `Altura calculada: ${avg} m` },
        { input: 'Si es la altura más alta de la categoría', result: `Puntaje del equipo: ${maxScore} (completo)` },
        { input: 'Si otro equipo llegó más alto, por ejemplo a 220 m', result: `Puntaje del equipo: ${round2((avg / 220) * maxScore)} de ${maxScore} (proporcional)` },
      ];
    },
  },
};
